// Precondition: None
// Postcondition: Custom element ft-filings-timeline registered
// Invariant: Shadow DOM contains timeline with 3D push buttons

import { fetchSubmissions } from '../../lib/financeapis.js';
import { createLogger } from '../../lib/logger.js';
import { parseEdgarSubmissions, groupByYear } from '../../lib/parsers.js';

const log = createLogger('ft-filings-timeline');

type TimelineState = 'idle' | 'loading' | 'error' | 'empty';

interface FilingPeriod {
  year: string;
  quarters: { label: string; href: string; restricted: boolean }[];
}

class FilingsTimeline extends HTMLElement {
  private data: FilingPeriod[] = [];
  private timelineState: TimelineState = 'idle';

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["items", "cik"];
  }

  get state(): TimelineState {
    return this.timelineState;
  }

  set state(value: TimelineState) {
    this.timelineState = value;
    this.render();
  }

  connectedCallback(): void {
    log.debug('connected');
    this.parseItems();
    this.render();

    // Self-fetch if cik is set but items isn't
    const cik = this.getAttribute('cik');
    const items = this.getAttribute('items');
    if (cik && !items) {
      this.loadFromCik(cik);
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || !this.shadowRoot) return;

    if (name === "items") {
      this.parseItems();
      this.render();
      return;
    }

    if (name === "cik" && newValue && !this.getAttribute('items')) {
      this.loadFromCik(newValue);
    }
  }

  // Precondition: cik is set
  // Postcondition: Fetches submissions and populates timeline
  private async loadFromCik(cik: string): Promise<void> {
    log.log('loadFromCik', { cik });
    this.state = 'loading';

    const result = await fetchSubmissions(cik);

    if (!result.ok) {
      log.error('loadFromCik failed', { cik, error: result.error });
      this.state = 'error';
      return;
    }

    const submissions = parseEdgarSubmissions(result.data);
    if (!submissions.length) {
      this.state = 'empty';
      return;
    }

    const yearMap = groupByYear(submissions);
    const years = Object.keys(yearMap).sort((a, b) => Number(b) - Number(a));
    this.data = years.map((year) => ({
      year,
      quarters: Array.from(yearMap[year].values())
        .sort((a, b) => a.quarter.localeCompare(b.quarter))
        .map((info) => ({
          label: info.quarter,
          href: `/companies/${cik}/${year}-${info.quarter}`,
          restricted: info.restricted,
        })),
    }));

    log.debug('loadFromCik success', { periods: this.data.length });
    this.state = 'idle';
  }

  // Precondition: items attribute contains valid JSON array
  // Postcondition: this.data populated with parsed filing periods
  private parseItems(): void {
    const itemsAttr = this.getAttribute("items");
    if (itemsAttr) {
      try {
        this.data = JSON.parse(itemsAttr);
        log.debug('parseItems', { periods: this.data.length });
      } catch (err) {
        log.warn('parseItems failed', { error: err });
        this.data = [];
      }
    }
  }

  // Precondition: None
  // Postcondition: items set from external data
  public setItems(items: FilingPeriod[]): void {
    log.debug('setItems', { periods: items.length });
    this.data = items;
    this.render();
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        --timeline-primary: var(--primary, #E0524D);
        --timeline-primary-dark: var(--primary-dark, #c73e39);
        --timeline-primary-edge: var(--primary-edge, hsl(4deg 70% 42%));
        --timeline-primary-edge-dark: var(--primary-edge-dark, hsl(4deg 70% 28%));
        --timeline-bg: var(--surface, #ffffff);
        --timeline-text: var(--heading, #111827);
        --timeline-border: var(--border-subtle, #F0F0F2);
      }

      @media (prefers-color-scheme: dark) {
        :host {
          --timeline-bg: var(--surface, #14161a);
          --timeline-text: var(--heading, #ffffff);
          --timeline-border: var(--border-subtle, #1b1d23);
        }
      }

      .timeline {
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      .empty-state {
        padding: 32px 24px;
        text-align: center;
        font: 400 14px/1.5 var(--font-sans, Inter, system-ui, sans-serif);
        color: var(--muted, #6b7280);
      }

      .empty-state.error {
        color: var(--danger, #dc2626);
      }

      .item {
        display: flex;
        align-items: baseline;
        gap: 32px;
        padding: 28px 0;
        border-bottom: 1px solid var(--timeline-border);
        overflow: hidden;
      }

      .item:last-child {
        border-bottom: none;
      }

      .year-wrap {
        min-width: 120px;
        overflow: hidden;
      }

      .year {
        font: 700 48px/1 var(--font-display, "Space Grotesk", system-ui, sans-serif);
        color: var(--timeline-text);
        letter-spacing: -0.03em;
        transform: translateY(100%);
        opacity: 0;
        animation: slideUpReveal 600ms cubic-bezier(0.3, 0.7, 0.4, 1) forwards;
        will-change: transform, opacity;
      }

      @keyframes slideUpReveal {
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .quarters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        padding-top: 8px;
      }

      /* 3D Push Button */
      .btn-3d {
        position: relative;
        border: none;
        background: transparent;
        padding: 0;
        cursor: pointer;
        outline-offset: 4px;
        transition: filter 250ms;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
        text-decoration: none;
        opacity: 0;
        transform: translateY(16px);
        animation: chipSlideUp 400ms ease-out forwards;
      }

      .btn-3d:focus-visible {
        outline: 2px solid var(--timeline-primary);
        outline-offset: 4px;
      }

      .btn-3d:focus:not(:focus-visible) {
        outline: none;
      }

      @keyframes chipSlideUp {
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .shadow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 8px;
        background: hsl(0deg 0% 0% / 0.2);
        filter: blur(3px);
        will-change: transform;
        transform: translateY(2px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      .edge {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 8px;
        background: linear-gradient(
          to left,
          var(--timeline-primary-edge-dark) 0%,
          var(--timeline-primary-edge) 8%,
          var(--timeline-primary-edge) 92%,
          var(--timeline-primary-edge-dark) 100%
        );
      }

      .front {
        display: block;
        position: relative;
        padding: 10px 18px;
        border-radius: 8px;
        font: 600 14px/1 var(--font-sans, Inter, system-ui, sans-serif);
        color: white;
        background: var(--timeline-primary);
        will-change: transform;
        transform: translateY(-4px);
        transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
      }

      .btn-3d:hover {
        filter: brightness(110%);
      }

      .btn-3d:hover .front {
        transform: translateY(-6px);
        transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      .btn-3d:active .front {
        transform: translateY(-2px);
        transition: transform 34ms;
      }

      .btn-3d:hover .shadow {
        transform: translateY(4px);
        transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
      }

      .btn-3d:active .shadow {
        transform: translateY(1px);
        transition: transform 34ms;
      }

      /* Staggered animations */
      .item:nth-child(1) .year { animation-delay: 0ms; }
      .item:nth-child(2) .year { animation-delay: 100ms; }
      .item:nth-child(3) .year { animation-delay: 200ms; }
      .item:nth-child(4) .year { animation-delay: 300ms; }
      .item:nth-child(5) .year { animation-delay: 400ms; }
      .item:nth-child(6) .year { animation-delay: 500ms; }
      .item:nth-child(7) .year { animation-delay: 600ms; }
      .item:nth-child(8) .year { animation-delay: 700ms; }

      .item:nth-child(1) .btn-3d:nth-child(1) { animation-delay: 150ms; }
      .item:nth-child(1) .btn-3d:nth-child(2) { animation-delay: 200ms; }
      .item:nth-child(1) .btn-3d:nth-child(3) { animation-delay: 250ms; }
      .item:nth-child(1) .btn-3d:nth-child(4) { animation-delay: 300ms; }
      .item:nth-child(2) .btn-3d:nth-child(1) { animation-delay: 250ms; }
      .item:nth-child(2) .btn-3d:nth-child(2) { animation-delay: 300ms; }
      .item:nth-child(2) .btn-3d:nth-child(3) { animation-delay: 350ms; }
      .item:nth-child(2) .btn-3d:nth-child(4) { animation-delay: 400ms; }
      .item:nth-child(3) .btn-3d:nth-child(1) { animation-delay: 350ms; }
      .item:nth-child(3) .btn-3d:nth-child(2) { animation-delay: 400ms; }
      .item:nth-child(3) .btn-3d:nth-child(3) { animation-delay: 450ms; }
      .item:nth-child(3) .btn-3d:nth-child(4) { animation-delay: 500ms; }
      .item:nth-child(4) .btn-3d:nth-child(1) { animation-delay: 450ms; }
      .item:nth-child(4) .btn-3d:nth-child(2) { animation-delay: 500ms; }
      .item:nth-child(4) .btn-3d:nth-child(3) { animation-delay: 550ms; }
      .item:nth-child(4) .btn-3d:nth-child(4) { animation-delay: 600ms; }

      @media (max-width: 640px) {
        .item {
          flex-direction: column;
          gap: 16px;
        }

        .year {
          font-size: 36px;
        }

        .quarters {
          padding-top: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .year,
        .btn-3d {
          animation: none;
          opacity: 1;
          transform: none;
        }

        .front,
        .shadow {
          transition: none;
        }
      }

      /* Locked button state */
      .btn-3d.locked {
        pointer-events: none;
        cursor: default;
        opacity: 0.6;
      }

      .btn-3d.locked .front {
        background: var(--muted, #6b7280);
      }

      .btn-3d.locked .edge {
        background: linear-gradient(
          to left,
          hsl(0deg 0% 30%) 0%,
          hsl(0deg 0% 40%) 8%,
          hsl(0deg 0% 40%) 92%,
          hsl(0deg 0% 30%) 100%
        );
      }

      /* Locked year state (all quarters locked) */
      .item.locked {
        opacity: 0.5;
      }

      .item.locked .year {
        color: var(--muted, #6b7280);
      }

      .locked-cta {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid var(--timeline-border);
      }
    `;
  }

  private getTemplate(): string {
    // Handle loading/error/empty states
    if (this.timelineState === 'loading') {
      return `<div class="timeline"><div class="empty-state"><ft-spinner text="Loading filings..."></ft-spinner></div></div>`;
    }
    if (this.timelineState === 'error') {
      return `<div class="timeline"><div class="empty-state error">Failed to load filings</div></div>`;
    }
    if (this.timelineState === 'empty' || this.data.length === 0) {
      return `<div class="timeline"><div class="empty-state">No filings available</div></div>`;
    }

    let hasLockedQuarters = false;

    const items = this.data.map((period) => {
      const allRestricted = period.quarters.every((q) => q.restricted);
      const anyRestricted = period.quarters.some((q) => q.restricted);
      if (anyRestricted) hasLockedQuarters = true;

      const buttons = period.quarters
        .map(
          (q) => `
          <a href="${q.restricted ? '#' : q.href}" class="btn-3d${q.restricted ? ' locked' : ''}"${q.restricted ? ' aria-disabled="true"' : ''}>
            <span class="shadow"></span>
            <span class="edge"></span>
            <span class="front">${q.label}</span>
          </a>
        `
        )
        .join("");

      return `
        <div class="item${allRestricted ? ' locked' : ''}">
          <div class="year-wrap">
            <div class="year">${period.year}</div>
          </div>
          <div class="quarters">
            ${buttons}
          </div>
        </div>
      `;
    }).join("");

    const cta = hasLockedQuarters ? `
      <div class="locked-cta">
        <ft-signup-cta
          heading="Unlock 20+ years of archives"
          subtext="Teams members get full historical access to track long-term investment patterns."
          button-text="View Plans"
          button-href="/pricing"
        ></ft-signup-cta>
      </div>
    ` : '';

    return `
      <div class="timeline" role="list" aria-label="Filing timeline">
        ${items}
        ${cta}
      </div>
    `;
  }

  private render(): void {
    if (!this.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(template.content.cloneNode(true));
  }
}

// Register custom element with guard
if (!customElements.get("ft-filings-timeline")) {
  customElements.define("ft-filings-timeline", FilingsTimeline);
}
